import { createSignal } from "solid-js";
import { config, isAuthConfigured, redirectUri, zitadelScopes } from "./config";
import { createPkcePair } from "./pkce";

const STORAGE_KEY = "deb-connect-auth";
const PKCE_KEY = "deb-connect-pkce";
const OAUTH_STATE_KEY = "deb-connect-oauth-state";
const RETURN_KEY = "deb-connect-return";

/** Refresh a bit before expiry so in-flight requests don't race the clock. */
const EXPIRY_SKEW_MS = 30_000;

export type AuthSession = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt: number;
};

const [session, setSessionSignal] = createSignal<AuthSession | null>(loadSession());

let refreshInFlight: Promise<AuthSession | null> | null = null;

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  return window.localStorage;
}

function loadSession(): AuthSession | null {
  const store = storage();
  if (!store) return null;

  // One-time migrate from sessionStorage (old ephemeral sessions).
  if (typeof window !== "undefined") {
    const legacy = sessionStorage.getItem(STORAGE_KEY);
    if (legacy && !store.getItem(STORAGE_KEY)) {
      store.setItem(STORAGE_KEY, legacy);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  const raw = store.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed.accessToken && !parsed.refreshToken) {
      store.removeItem(STORAGE_KEY);
      return null;
    }
    // Keep expired access tokens if we can refresh them later.
    if (parsed.expiresAt && parsed.expiresAt < Date.now() && !parsed.refreshToken) {
      store.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistSession(next: AuthSession | null) {
  const store = storage();
  if (!store) return;
  if (!next) {
    store.removeItem(STORAGE_KEY);
    return;
  }
  store.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function getSession(): AuthSession | null {
  return session();
}

/** True when we have a usable access token, or a refresh token we can renew with. */
export function isAuthenticated(): boolean {
  const s = session();
  if (!s) return false;
  if (s.accessToken && s.expiresAt > Date.now()) return true;
  return Boolean(s.refreshToken);
}

export function getAccessToken(): string | null {
  const s = session();
  if (!s || !s.accessToken || s.expiresAt <= Date.now()) return null;
  return s.accessToken;
}

export function setSession(next: AuthSession | null) {
  persistSession(next);
  setSessionSignal(next);
}

type TokenResponse = {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
};

function sessionFromTokens(tokens: TokenResponse, previous?: AuthSession | null): AuthSession {
  return {
    accessToken: tokens.access_token,
    // Zitadel may omit refresh_token on refresh; keep the previous one.
    refreshToken: tokens.refresh_token ?? previous?.refreshToken,
    idToken: tokens.id_token ?? previous?.idToken,
    expiresAt: Date.now() + tokens.expires_in * 1000 - EXPIRY_SKEW_MS,
  };
}

async function refreshSession(): Promise<AuthSession | null> {
  const current = session();
  if (!current?.refreshToken) {
    setSession(null);
    return null;
  }

  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: config.zitadelClientId,
        refresh_token: current.refreshToken!,
      });

      const resp = await fetch(`${config.zitadelIssuer}/oauth/v2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      if (!resp.ok) {
        setSession(null);
        return null;
      }

      const tokens = (await resp.json()) as TokenResponse;
      const next = sessionFromTokens(tokens, current);
      setSession(next);
      return next;
    } catch {
      setSession(null);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Ensures a valid access token, refreshing via offline_access when needed.
 * Use in route guards and API calls so revisits stay signed in for the refresh lifetime.
 */
export async function ensureAuthenticated(): Promise<boolean> {
  const s = session();
  if (!s) return false;
  if (s.accessToken && s.expiresAt > Date.now()) return true;
  if (!s.refreshToken) {
    setSession(null);
    return false;
  }
  const refreshed = await refreshSession();
  return Boolean(refreshed?.accessToken);
}

/** Returns a valid access token, refreshing if the current one is expired. */
export async function ensureAccessToken(): Promise<string | null> {
  const ok = await ensureAuthenticated();
  if (!ok) return null;
  return getAccessToken();
}

function randomOAuthState(): string {
  if (typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch {
      // Some WebViews expose randomUUID but throw outside a secure context.
    }
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

const returnMemory = new Map<string, string>();

function returnStore(): { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void } {
  if (typeof sessionStorage !== "undefined") return sessionStorage;
  return {
    getItem: (k) => returnMemory.get(k) ?? null,
    setItem: (k, v) => {
      returnMemory.set(k, v);
    },
    removeItem: (k) => {
      returnMemory.delete(k);
    },
  };
}

/** Same-tab path to resume after Zitadel (e.g. `/meetup/:slug`). */
export function rememberReturnTo(path: string): void {
  const trimmed = path.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return;
  returnStore().setItem(RETURN_KEY, trimmed);
}

export function takeReturnTo(): string | null {
  const store = returnStore();
  const path = store.getItem(RETURN_KEY);
  store.removeItem(RETURN_KEY);
  if (!path || !path.startsWith("/") || path.startsWith("//")) return null;
  return path;
}

export async function startLogin(): Promise<void> {
  if (!isAuthConfigured()) {
    throw new Error("Zitadel is not configured. Set VITE_ZITADEL_CLIENT_ID in .env.local");
  }

  const { verifier, challenge } = await createPkcePair();
  // PKCE verifier must survive the OAuth redirect in the same tab only.
  sessionStorage.setItem(PKCE_KEY, verifier);

  const state = randomOAuthState();
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: config.zitadelClientId,
    redirect_uri: redirectUri(),
    response_type: "code",
    scope: zitadelScopes(),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.assign(`${config.zitadelIssuer}/oauth/v2/authorize?${params}`);
}

export async function handleAuthCallback(code: string, state: string): Promise<void> {
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(PKCE_KEY);

  if (!expectedState || expectedState !== state) {
    throw new Error("Invalid OAuth state");
  }
  if (!verifier) {
    throw new Error("Missing PKCE verifier");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.zitadelClientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });

  const resp = await fetch(`${config.zitadelIssuer}/oauth/v2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Token exchange failed: ${text}`);
  }

  const tokens = (await resp.json()) as TokenResponse;
  setSession(sessionFromTokens(tokens));
}

export function logout(): void {
  setSession(null);
  if (typeof window !== "undefined") {
    window.location.assign("/");
  }
}
